<template>
  <div>
    <div class="no-date-selected" v-if="!selectedActivityDate && !packages.length && !packagesLoading">
      <span class="icon icon-info"/>
      <span>{{t('Select a date to see available packages and pricing')}}</span>
    </div>
    <section class="property-details block-split" id="entranceTicket">
      <h2 class="text-title-2 fw-semi-bold">{{t('Activity package')}}</h2>
      <div class="container">
        <div class="activity-packages" v-if="packages.length">
          <div
              v-if="selectedActivityDate"
              class="activity-package--date"
          >{{ dayjs(selectedActivityDate).format('dddd, MMMM DD, YYYY') }}</div>
          <hr>
          <div class="activity-package--cancellation">
            <div class="free-cancellation" v-if="fullCancellation.period">
              <img src="/images/check.svg" alt="check">
              You can cancel your tour until {{duration(fullCancellation.period)}} before your travel date
            </div>
            <div v-else>
              No refunds
            </div>
          </div>
          <hr>
          <div class="activity-packages-container" v-for="item in packages" :key="item.id">
            <ActivityPackage :package="item" />
          </div>
        </div>
        <div v-else-if="packagesLoading" class="packages-empty-state">
          <div class="packages-empty-state__content">
            <span class="icon icon-loading packages-empty-state__icon"></span>
            <div class="packages-empty-state__text">
              <div class="text-body-1 fw-semi-bold">{{$t('Checking availability...')}}</div>
            </div>
          </div>
        </div>
        <!-- A date was chosen and the supplier came back with nothing. This used to fall through
             to "Choose a date to get started" — telling the user to do the thing they had just
             done, with no hint that the date itself was the problem. -->
        <div v-else-if="isSoldOut" class="packages-empty-state packages-empty-state--sold-out">
          <div class="packages-empty-state__content">
            <span class="icon icon-info packages-empty-state__icon"></span>
            <div class="packages-empty-state__text">
              <div class="text-body-1 fw-semi-bold">
                {{$t('Sold out on')}} {{ dayjs(selectedActivityDate).format('dddd, MMMM DD, YYYY') }}
              </div>
              <div class="text-body-2 color-dark-grey">{{$t('This activity has no availability left on that date. Try another day.')}}</div>
            </div>
          </div>
          <button class="btn btn-primary packages-empty-state__btn" @click="openDatesSelector">
            {{$t('Choose another date')}}
          </button>
        </div>
        <div v-else class="packages-empty-state">
          <div class="packages-empty-state__content">
            <span class="icon icon-calendar packages-empty-state__icon"></span>
            <div class="packages-empty-state__text">
              <div class="text-body-1 fw-semi-bold">{{$t('Choose a date to get started')}}</div>
              <div class="text-body-2 color-dark-grey">{{$t('Select your preferred date to see available packages and pricing')}}</div>
            </div>
          </div>
          <button class="btn btn-primary packages-empty-state__btn" @click="openDatesSelector">
            {{$t('Select a date')}}
          </button>
        </div>
      </div>
    </section>
  </div>
</template>
<script setup lang="ts">
import dayjs from '~/utils/dayjs'
import ActivityPackage from "~/components/ActivityPropertyPage/ActivityPackage.vue";
import { useSearchStore } from "~/store/search";
import { useActivityPropertyStore } from "~/store/activityProperty";
const activityPropertyStore = useActivityPropertyStore();
const searchStore = useSearchStore()
const propertiesStore = useActivityPropertyStore()
const { t } = useI18n()
const packages = computed(() => activityPropertyStore.packages)
const packagesLoading = computed(() => activityPropertyStore.packagesLoading)
const selectedActivityDate = computed(() => searchStore.activitySearchDate);
// Only claim "sold out" once a lookup for THIS date has actually completed and come back
// empty — never while nothing has been asked for yet.
const isSoldOut = computed(() =>
  !!selectedActivityDate.value && activityPropertyStore.packagesChecked && !packages.value.length
)
const property = computed(() => propertiesStore.property)
const fullCancellation = computed(() => {
  const cancellations = property.value.cancellation_policies
  return cancellations?.find((cancellation: any) => {return cancellation.refund_value == 100 && cancellation.refund_type == 'percentage'}) || {}
})

const duration = (durationStr:string) => {
  return dayjs.duration(durationStr).humanize()
}

function openDatesSelector() {
  setTimeout(() => {
    // On mobile `.dates-selection` is display:none and the date is chosen through the sticky
    // footer button instead, so clicking the desktop control there is a no-op. Fall back.
    const desktop = document.querySelector<HTMLElement>('button.dates-selection')
    if (desktop && desktop.offsetParent !== null) {
      desktop.click()
      return
    }
    document.querySelector<HTMLElement>('button.checkout-button--select-date')?.click()
  }, 100)
}

watch(packages, (newVal) => {
  if (newVal && newVal.length > 0) {
    propertiesStore.setSelectedPackageId(newVal[0].id)
  }
})

</script>


<style lang="scss">
.activity-packages {
  background: var(--light-grey);
  padding: 16px;
  border-radius: 10px;
  margin-bottom: 16px;

  &-container {
    margin-top: 12px;
  }
}
.packages-empty-state {
  background: var(--light-grey);
  border-radius: 10px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  &--sold-out .packages-empty-state__icon {
    color: var(--dark-grey);
  }

  &__content {
    display: flex;
    gap: 12px;
    align-items: flex-start;
  }

  &__icon {
    font-size: 24px;
    color: var(--blue);
    flex-shrink: 0;
    margin-top: 2px;
  }

  &__text {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__btn {
    align-self: flex-start;
    height: 44px;
    padding: 0 24px;

    @media screen and (max-width: 768px) {
      width: 100%;
    }
  }
}

.no-date-selected {
  padding: 12px 16px;
  border-radius: 10px;
  background: var(--blue-highlight);
  margin-bottom: 24px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--black);
  .icon {
    font-size: 20px;
    color: var(--blue);
  }
}

.activity-package--cancellation {
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--dark-grey);
  padding: 12px 0;

  .free-cancellation {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #2e7d32;
    font-weight: 500;
  }
}

</style>
